import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  XMarkIcon,
  ChevronUpDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

const Select = forwardRef(
  (
    {
      options = [],
      multiple = false,
      searchable = false,
      placeholder = "Select...",
      className = "",
      containerClassName = "",
      error = false,
      disabled = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState(multiple ? [] : "");
    const [searchQuery, setSearchQuery] = useState("");
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useImperativeHandle(ref, () => ({
      value: selectedValues,
      reset: () => setSelectedValues(multiple ? [] : ""),
    }));

    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const isSelected = (value) => {
      return multiple
        ? selectedValues.includes(value)
        : selectedValues === value;
    };

    const toggleOption = (value) => {
      if (multiple) {
        const newValues = selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];
        setSelectedValues(newValues);
        onChange?.(newValues);
      } else {
        const newValue = selectedValues === value ? "" : value;
        setSelectedValues(newValue);
        onChange?.(newValue);
        setIsOpen(false);
      }
    };

    const removeValue = (value, e) => {
      e.stopPropagation();
      const newValues = selectedValues.filter((v) => v !== value);
      setSelectedValues(newValues);
      onChange?.(newValues);
    };

    return (
      <div
        ref={containerRef}
        className={`relative w-full ${containerClassName}`}
      >
        <div
          className={`
                flex items-center justify-between w-full
                rounded-md border text-sm ${
                  error ? "border-red-500" : "border-zinc-200"
                } bg-white
                ${disabled ? "bg-zinc-50 cursor-not-allowed" : "cursor-pointer"}
                ${isOpen ? "ring-2 ring-zinc-900/20" : ""}
                transition-all duration-150
                min-h-10
                ${className}
              `}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex flex-wrap items-center gap-2 flex-1 p-2">
            {multiple ? (
              selectedValues.length > 0 ? (
                selectedValues.map((value) => {
                  const option = options.find((o) => o.value === value);
                  return (
                    <div
                      key={value}
                      className="flex items-center gap-1 bg-zinc-100 px-2 py-1 rounded text-xs"
                    >
                      <span className="text-zinc-700">{option?.label}</span>
                      <XMarkIcon
                        className="w-4 h-4 text-zinc-400 hover:text-zinc-600"
                        onClick={(e) => removeValue(value, e)}
                      />
                    </div>
                  );
                })
              ) : (
                <span className="text-zinc-400 ml-2 py-1">{placeholder}</span>
              )
            ) : (
              <span className="text-zinc-900 ml-2">
                {options.find((o) => o.value === selectedValues)?.label ||
                  placeholder}
              </span>
            )}
          </div>

          <div className="flex items-center pr-2">
            <ChevronUpDownIcon className="w-5 h-5 text-zinc-400" />
          </div>
        </div>

        {isOpen && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-zinc-100">
            {searchable && (
              <div className="p-2 border-b border-zinc-100">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search..."
                  className="w-full p-2 text-xs rounded-md border border-zinc-200 focus:ring-2 focus:ring-zinc-900/20"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`
                        flex items-center justify-between p-2
                        hover:bg-zinc-50 cursor-pointer text-sm
                        ${isSelected(option.value) ? "bg-zinc-100" : ""}
                        transition-colors duration-100
                      `}
                  onClick={() => toggleOption(option.value)}
                >
                  <span className="text-zinc-900">{option.label}</span>
                  {isSelected(option.value) && (
                    <CheckIcon className="w-4 h-4 text-zinc-900" />
                  )}
                </div>
              ))}

              {filteredOptions.length === 0 && (
                <div className="p-2 text-center text-zinc-400">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
